// The Groups tab: the list of groups, and the sheets for a new group, an invite, joining and your name.
import type { Group } from "../../shared/model.ts";
import { $, $maybe, el, icon, input, toast } from "../dom.ts";
import { dFmt, t, tFmt } from "../i18n.ts";
import { cancelButton, closeSheet, grow, openSheet, primaryButton } from "../sheet.ts";
import { onRender } from "../state.ts";
import { errorMessage, errorName } from "../util.ts";
import { setView } from "../views.ts";
import { createGroup, groups, inviteLink, joinGroup, loadGroups, me, parseInvite, saveMe, startPolling, syncAll, updateBadge, type Invite } from "./store.ts";
import { openChat } from "./chat.ts";

const timeShort = (ts: number) => (Date.now() - ts < 864e5 && new Date(ts).getDate() === new Date().getDate() ? tFmt : dFmt).format(ts);

export function renderGroups(): void {
  const list = $maybe("#group-list");
  if (!list) return;
  $("#me-name").textContent = me.name || t("groupNoName");
  if (!groups.length) {
    list.replaceChildren(el("div", { class: "empty" }, icon("group"), el("h2", { text: t("groupsEmptyTitle") }), el("p", { text: t("groupsEmptyText") })));
    return;
  }
  const sorted = [...groups].sort((a, b) => (b.lastAt || b.createdAt) - (a.lastAt || a.createdAt));
  list.replaceChildren(...sorted.map((g) => el("button", { class: "gitem", type: "button", onclick: () => void openChat(g.id) },
    el("span", { class: "gavatar", "aria-hidden": "true", text: (g.name || "?").trim().charAt(0).toUpperCase() }),
    el("span", { class: "gmain" }, el("b", { text: g.name }), el("small", { text: g.broken ? t("groupBroken") : (g.lastText || t("groupNoMessages")) })),
    el("span", { class: "gside" }, g.lastAt ? el("small", { class: "num", text: timeShort(g.lastAt) }) : null,
      g.unread ? el("span", { class: "gunread num", text: String(g.unread) }) : null))));
}
onRender(renderGroups);

/* ---------- your name ---------- */
const nameField = () => el("label", { class: "f" }, el("span", { class: "lbl", text: t("groupYourName") }),
  el("input", { type: "text", id: "g-me", maxlength: "30", autocomplete: "nickname", value: me.name, placeholder: t("groupYourNamePh") }));
/** Takes the name from the name field; false (and a hint) when it's empty. */
function takeName(): boolean {
  const field = $maybe<HTMLInputElement>("#g-me"), v = field?.value.trim();
  if (!v) { field?.focus(); toast(t("groupNeedName")); return false; }
  me.name = v;
  saveMe();
  return true;
}
function openRename(): void {
  openSheet(t("groupChangeName"), [nameField(), el("p", { class: "hint", text: t("groupNameHint") })],
    [grow(), primaryButton(t("save"), () => { if (takeName()) { closeSheet(); renderGroups(); } })]);
}

/* ---------- new group and invite ---------- */
function openNewGroup(): void {
  openSheet(t("groupNew"), [
    el("label", { class: "f" }, el("span", { class: "lbl", text: t("groupName") }),
      el("input", { type: "text", id: "g-name", maxlength: "40", autocomplete: "off", placeholder: t("groupNamePh") })),
    nameField(),
    el("p", { class: "hint", text: t("groupNewHint") }),
  ], [grow(), cancelButton(), primaryButton(t("groupCreate"), async (btn) => {
    const name = input("#g-name").value.trim();
    if (!name) { input("#g-name").focus(); toast(t("groupNeedGroupName")); return; }
    if (!takeName()) return;
    btn.disabled = true;
    try { const g = await createGroup(name); renderGroups(); openInvite(g); }
    catch (err) { console.error(err); toast(errorName(err) === "TimeoutError" ? t("saveStuck") : t("groupOffline")); btn.disabled = false; }
  })]);
  setTimeout(() => $maybe("#g-name")?.focus(), 50);
}
function qrCode(text: string): HTMLElement | null {
  if (typeof qrcode === "undefined") return null; // the QR library didn't load (offline)
  const q = qrcode(0, "M");
  q.addData(text);
  q.make();
  const box = el("div", { class: "qr" });
  box.innerHTML = q.createSvgTag({ cellSize: 5, margin: 3, scalable: true });
  return box;
}
export function openInvite(g: Group): void {
  const link = inviteLink(g);
  const code = el("code", { class: "invite-link", text: link });
  const copy = el("button", { class: "btn", type: "button", onclick: async (e) => {
    try { await navigator.clipboard.writeText(link); e.currentTarget.textContent = t("copied"); }
    catch { const r = document.createRange(); r.selectNodeContents(code); const s = getSelection(); s?.removeAllRanges(); s?.addRange(r); }
  } }, t("copyLink"));
  const share = typeof navigator.share === "function"
    ? primaryButton(t("shareLink"), () => navigator.share({ title: t("groupInviteShareTitle", g.name), text: t("groupInviteShareText", g.name), url: link }).catch(() => {}))
    : primaryButton(t("done"), closeSheet);
  openSheet(t("groupInviteTitle", g.name), [
    el("p", { text: t("groupInviteText") }),
    qrCode(link),
    code,
    el("p", { class: "hint warn", text: t("groupInviteWarn") }),
  ], [copy, grow(), share]);
}

/* ---------- joining ---------- */
function openJoin(inv: Invite): void {
  const known = groups.find((x) => x.id === inv.id);
  if (known) { void openChat(known.id); return; }
  openSheet(t("groupJoinTitle"), [
    el("p", { class: "join-name", text: inv.name || t("groupUnnamed") }),
    el("p", { text: t("groupJoinText") }),
    nameField(),
  ], [grow(), cancelButton(), primaryButton(t("groupJoin"), async (btn) => {
    if (!takeName()) return;
    btn.disabled = true;
    try { const g = await joinGroup(inv); closeSheet(); renderGroups(); setView("groups"); void openChat(g.id); }
    catch (err) { console.error(err); toast(errorMessage(err) === "bad link" ? t("groupBadLink") : t("groupOffline")); btn.disabled = false; }
  })]);
}
function openPasteJoin(): void {
  openSheet(t("groupJoinTitle"), [
    el("label", { class: "f" }, el("span", { class: "lbl", text: t("groupPasteLbl") }),
      el("textarea", { id: "g-paste", rows: "3", placeholder: t("groupPastePh") })),
  ], [grow(), cancelButton(), primaryButton(t("next"), () => {
    const inv = parseInvite($<HTMLTextAreaElement>("#g-paste").value.trim());
    if (!inv) { toast(t("groupBadLink")); return; }
    openJoin(inv);
  })]);
}
$("#group-new").addEventListener("click", openNewGroup);
$("#group-join").addEventListener("click", openPasteJoin);
$("#me-change").addEventListener("click", openRename);

/** An invite link opened in the app (the key is in the # part). */
function checkInviteInUrl(): void {
  const inv = parseInvite(location.hash);
  if (!inv) return;
  history.replaceState(null, "", location.pathname + location.search); // keep the key out of the address bar
  openJoin(inv);
}
addEventListener("hashchange", checkInviteInUrl);

export async function groupsStart(): Promise<void> {
  await loadGroups();
  renderGroups();
  updateBadge();
  startPolling();
  void syncAll();
  checkInviteInUrl();
}
